import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart, useAuth } from "../context/AppContext";
import { createOrder } from "../lib/dataStore";
import { calculateCodCharge } from "../lib/orderHelpers";
import { buildWhatsAppOrderUrl } from "../lib/whatsapp";
import {
  Banknote,
  CreditCard,
  Package,
  ShieldCheck,
  CheckCircle2,
  Send,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, shippingTotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  const [paymentMode, setPaymentMode] = useState("online"); // 'online' | 'cod'
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customerName: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    notes: "",
  });

  // Pre-fill user data when logged in
  useEffect(() => {
    if (user) {
      setForm((prev) => ({
        ...prev,
        customerName: prev.customerName || user.displayName || "",
        email: prev.email || user.email || "",
      }));
    }
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // COD Calculation: ₹50 or 3% of total bill, whichever is higher
  const isCod = paymentMode === "cod";
  const totalBillBeforeCod = subtotal + shippingTotal;
  const codCharge = isCod ? calculateCodCharge(totalBillBeforeCod) : 0;
  const totalOrderAmount = totalBillBeforeCod + codCharge;
  const advanceRequired = isCod ? Math.min(100, totalOrderAmount) : 0;
  const balanceDue = isCod ? Math.max(0, totalOrderAmount - advanceRequired) : 0;

  const handleSubmitOrder = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      return toast.error("Your cart is empty");
    }

    if (!form.customerName.trim()) {
      return toast.error("Please enter your full name");
    }

    const cleanPhone = form.phone.replace(/\D/g, "");
    if (!cleanPhone || cleanPhone.length < 10) {
      return toast.error("Please enter a valid 10-digit mobile number");
    }

    if (!form.address.trim()) {
      return toast.error("Please enter your full delivery address");
    }

    if (!form.city.trim()) {
      return toast.error("Please enter your city");
    }

    const cleanPincode = form.pincode.replace(/\D/g, "");
    if (!cleanPincode || cleanPincode.length < 6) {
      return toast.error("Please enter a valid 6-digit postal pincode");
    }

    setSubmitting(true);

    try {
      const orderPayload = {
        customerName: form.customerName.trim(),
        phone: cleanPhone,
        email: form.email.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        pincode: cleanPincode,
        notes: form.notes?.trim() || "",
        orderMode: "Online",
        userId: user?.uid || null,
        items: items.map((i) => ({
          id: i.id,
          variantTag: i.variantTag || "Default",
          name: i.name,
          price: Number(i.price),
          mrp: Number(i.mrp || i.price),
          shippingCharge: Number(i.shippingCharge || 0),
          quantity: Number(i.quantity),
        })),
        subtotal,
        shippingCharge: shippingTotal,
        codCharge: isCod ? codCharge : 0,
        advancePaid: isCod ? advanceRequired : 0,
        codBalanceDue: isCod ? balanceDue : 0,
        total: totalOrderAmount,
        paymentMethod: isCod ? "Cash on Delivery (COD)" : "Prepaid / Direct Pay",
        paymentType: isCod ? "COD" : "Prepaid",
        status: "New",
      };

      const savedOrder = await createOrder(orderPayload);

      // Build WhatsApp message with COD breakdown and total
      const waUrl = buildWhatsAppOrderUrl({
        order: { ...orderPayload, id: savedOrder.id },
        orderId: savedOrder.id,
      });

      clear();
      toast.success("Order recorded! Opening WhatsApp to confirm your order…");

      // Open WhatsApp in a new window/tab, and navigate to orders
      window.open(waUrl, "_blank");
      nav(`/orders`);
    } catch (err) {
      console.error("Order submission error:", err);
      toast.error(err.message || "Could not place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4 text-muted-foreground">
          <Package className="w-8 h-8" />
        </div>
        <h1 className="font-serif text-4xl mb-4">Your cart is empty</h1>
        <p className="text-muted-foreground mb-6">
          Head to the shop to discover our handcrafted Santiniketan collection.
        </p>
        <button
          data-testid="checkout-shop-btn"
          onClick={() => nav("/shop")}
          className="px-6 py-3 rounded-full bg-terracotta text-primary-foreground font-medium hover:opacity-95 transition"
        >
          Browse Shop
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
      {/* Header */}
      <div className="mb-8">
        <div className="overline text-terracotta mb-2 font-semibold tracking-wider">
          Checkout
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl text-foreground">
          Complete Your Order
        </h1>
        <p className="text-muted-foreground mt-2 text-sm sm:text-base">
          Fill in your delivery address and choose between Prepaid or Cash on Delivery (COD).
        </p>
      </div>

      <form onSubmit={handleSubmitOrder} className="grid lg:grid-cols-[1fr_420px] gap-10">
        {/* Left column: Address & Payment Methods */}
        <div className="space-y-8">
          {/* Shipping Address */}
          <section className="bg-card border border-border rounded-xl p-6 shadow-2xs">
            <h2 className="font-serif text-xl mb-4 text-foreground flex items-center gap-2">
              <span>1. Delivery Details</span>
            </h2>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Full Name *"
                  testid="co-name"
                  placeholder="e.g. Ananya Sen"
                  value={form.customerName}
                  onChange={set("customerName")}
                  required
                />
                <Field
                  label="Mobile Number (10 digits) *"
                  testid="co-phone"
                  type="tel"
                  placeholder="e.g. 9876543210"
                  value={form.phone}
                  onChange={set("phone")}
                  required
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Email Address (Optional)"
                  testid="co-email"
                  type="email"
                  placeholder="e.g. ananya@example.com"
                  value={form.email}
                  onChange={set("email")}
                />
                <Field
                  label="City / Town *"
                  testid="co-city"
                  placeholder="e.g. Kolkata"
                  value={form.city}
                  onChange={set("city")}
                  required
                />
              </div>

              <Field
                label="Full Street Address (House/Flat No, Landmark, Area) *"
                testid="co-address"
                placeholder="e.g. Flat 3B, Shantiniketan Enclave, Bolpur"
                value={form.address}
                onChange={set("address")}
                required
                as="textarea"
              />

              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="State *"
                  testid="co-state"
                  placeholder="e.g. West Bengal"
                  value={form.state}
                  onChange={set("state")}
                  required
                />
                <Field
                  label="Pincode (6 digits) *"
                  testid="co-pincode"
                  type="text"
                  maxLength={6}
                  placeholder="e.g. 700001"
                  value={form.pincode}
                  onChange={set("pincode")}
                  required
                />
              </div>

              <Field
                label="Order Notes / Delivery instructions (Optional)"
                testid="co-notes"
                placeholder="Any special handling instructions, preferred delivery time, etc."
                value={form.notes}
                onChange={set("notes")}
                as="textarea"
              />
            </div>
          </section>

          {/* Payment Method Selector */}
          <section className="bg-card border border-border rounded-xl p-6 shadow-2xs">
            <h2 className="font-serif text-xl mb-4 text-foreground flex items-center gap-2">
              <span>2. Choose Payment Method</span>
            </h2>

            <div className="grid gap-4">
              {/* Option 1: Prepaid / Online */}
              <label
                data-testid="payment-method-online"
                className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition ${
                  paymentMode === "online"
                    ? "border-terracotta bg-terracotta/5 shadow-xs"
                    : "border-border hover:border-muted-foreground/40 bg-card"
                }`}
              >
                <input
                  type="radio"
                  name="paymentMode"
                  value="online"
                  checked={paymentMode === "online"}
                  onChange={() => setPaymentMode("online")}
                  className="mt-1 w-4 h-4 text-terracotta accent-terracotta"
                />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-terracotta" />
                      <span className="font-semibold text-foreground text-sm sm:text-base">
                        Prepaid / Online Payment (UPI / QR / Bank)
                      </span>
                    </div>
                    <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                      No Extra Charges (₹0)
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                    Pay securely via UPI (Google Pay, PhonePe, Paytm, BHIM) or direct bank transfer on order confirmation.
                  </p>
                  <div className="mt-2 text-xs font-medium text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Free shipping eligible • Instant dispatch</span>
                  </div>
                </div>
              </label>

              {/* Option 2: Cash on Delivery (COD) */}
              <label
                data-testid="payment-method-cod"
                className={`relative flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition ${
                  paymentMode === "cod"
                    ? "border-terracotta bg-terracotta/5 shadow-xs"
                    : "border-border hover:border-muted-foreground/40 bg-card"
                }`}
              >
                <input
                  type="radio"
                  name="paymentMode"
                  value="cod"
                  checked={paymentMode === "cod"}
                  onChange={() => setPaymentMode("cod")}
                  className="mt-1 w-4 h-4 text-terracotta accent-terracotta"
                />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Banknote className="w-4 h-4 text-terracotta" />
                      <span className="font-semibold text-foreground text-sm sm:text-base">
                        Cash on Delivery (COD)
                      </span>
                    </div>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300">
                      COD Fee: +₹{calculateCodCharge(totalBillBeforeCod)}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                    Pay ₹100 advance online to confirm booking, then pay remaining balance in cash on delivery.
                  </p>
                  <div className="mt-2.5 p-3 rounded-xl bg-amber-50/90 dark:bg-amber-950/40 border border-amber-300/80 dark:border-amber-700/60 text-xs text-amber-950 dark:text-amber-100 space-y-2.5">
                    <div className="flex items-center gap-1.5 font-bold text-amber-950 dark:text-amber-100 text-xs sm:text-sm">
                      <Banknote className="w-4 h-4 text-terracotta shrink-0" />
                      <span>COD Advance Policy: ₹100 Prepaid + ₹{balanceDue} on Delivery</span>
                    </div>
                    <p className="leading-relaxed text-amber-900/90 dark:text-amber-200/90">
                      For all Cash on Delivery orders, <strong>₹100 is paid as advance</strong> to confirm and dispatch your order. The remaining balance of <strong>₹{balanceDue}</strong> is paid during delivery.
                    </p>
                    <div className="grid grid-cols-2 gap-2 pt-1 font-sans">
                      <div className="bg-amber-100/80 dark:bg-amber-900/50 border border-amber-300/70 dark:border-amber-700/50 p-2.5 rounded-lg text-center">
                        <span className="text-[10px] text-amber-800 dark:text-amber-300 font-bold uppercase tracking-wider block">
                          Prepaid Advance
                        </span>
                        <span className="font-price font-bold text-lg text-emerald-800 dark:text-emerald-400">
                          ₹{advanceRequired}
                        </span>
                        <span className="text-[10px] text-muted-foreground block mt-0.5">Paid via UPI to confirm</span>
                      </div>
                      <div className="bg-amber-100/80 dark:bg-amber-900/50 border border-amber-300/70 dark:border-amber-700/50 p-2.5 rounded-lg text-center">
                        <span className="text-[10px] text-amber-800 dark:text-amber-300 font-bold uppercase tracking-wider block">
                          Balance on Delivery
                        </span>
                        <span className="font-price font-bold text-lg text-terracotta">
                          ₹{balanceDue}
                        </span>
                        <span className="text-[10px] text-muted-foreground block mt-0.5">Cash to courier agent</span>
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground pt-1 border-t border-amber-200/70 dark:border-amber-800/40">
                      COD Fee: ₹{codCharge} (higher of ₹50 or 3% of items value). Grand total: ₹{totalOrderAmount}.
                    </div>
                  </div>
                </div>
              </label>
            </div>
          </section>
        </div>

        {/* Right column: Sticky Order Summary & Submit Button */}
        <aside className="border border-border rounded-xl p-6 h-max sticky top-24 bg-card shadow-2xs">
          <h3 className="font-serif text-2xl mb-4 text-foreground">Order Summary</h3>

          {/* Items summary */}
          <div className="space-y-3 mb-4 max-h-56 overflow-y-auto pr-1">
            {items.map((i) => (
              <div key={i.key || i.id} className="flex justify-between items-start text-sm gap-2">
                <div className="flex-1">
                  <span className="line-clamp-1 font-medium text-foreground">
                    {i.name} × {i.quantity}
                  </span>
                  {i.variantTag && i.variantTag !== "Default" && (
                    <span className="text-xs text-muted-foreground block">
                      Variant: {i.variantTag}
                    </span>
                  )}
                </div>
                <span className="font-price font-semibold text-foreground shrink-0">
                  ₹{Number(i.price) * Number(i.quantity)}
                </span>
              </div>
            ))}
          </div>

          {/* Pricing breakdown */}
          <div className="border-t border-border pt-4 space-y-2 text-sm mb-6">
            <div className="flex justify-between text-muted-foreground">
              <span>Items Subtotal</span>
              <span className="font-price font-semibold text-foreground">₹{subtotal}</span>
            </div>

            <div className="flex justify-between text-muted-foreground">
              <span>Shipping Fee</span>
              <span>
                {shippingTotal > 0 ? (
                  <span className="font-price font-semibold text-foreground">₹{shippingTotal}</span>
                ) : (
                  <span className="text-emerald-700 dark:text-emerald-400 font-semibold">FREE</span>
                )}
              </span>
            </div>

            {isCod && (
              <div className="space-y-2">
                <div className="flex justify-between items-center text-amber-800 dark:text-amber-300 font-medium bg-amber-50/70 dark:bg-amber-950/20 px-2 py-1.5 rounded-md border border-amber-200/60 dark:border-amber-800/40">
                  <div>
                    <span className="block text-xs font-semibold">Cash on Delivery (COD) Fee</span>
                    <span className="text-[10px] text-muted-foreground">₹50 or 3% of order (higher)</span>
                  </div>
                  <span className="font-price font-bold text-sm">+₹{codCharge}</span>
                </div>

                <div className="p-2.5 rounded-lg bg-amber-50/90 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 space-y-1.5 text-xs">
                  <div className="flex justify-between items-center text-emerald-800 dark:text-emerald-300 font-semibold">
                    <span>Prepaid Advance (To Confirm):</span>
                    <span className="font-price font-bold text-sm">₹{advanceRequired}</span>
                  </div>
                  <div className="flex justify-between items-center text-amber-900 dark:text-amber-200 font-bold">
                    <span>Balance Due on Delivery:</span>
                    <span className="font-price font-bold text-sm text-terracotta">₹{balanceDue}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center pt-3 border-t border-border text-base font-semibold">
              <span className="text-foreground">Total Order Amount</span>
              <span data-testid="co-total" className="font-price text-2xl font-bold text-terracotta">
                ₹{totalOrderAmount}
              </span>
            </div>

            <div className="text-xs text-muted-foreground text-right">
              Payment mode: <strong className="text-foreground">{isCod ? "Cash on Delivery" : "Prepaid"}</strong>
            </div>
          </div>

          {/* Action Button */}
          <button
            data-testid="place-order-btn"
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-4 rounded-full bg-terracotta text-white font-semibold hover:opacity-95 disabled:opacity-50 transition shadow-xs text-sm sm:text-base cursor-pointer"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Recording Order…</span>
              </>
            ) : isCod ? (
              <>
                <Send className="w-4 h-4" />
                <span>Place COD Order • ₹{advanceRequired} Advance</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Place Order via WhatsApp • ₹{totalOrderAmount}</span>
              </>
            )}
          </button>

          {/* Trust points */}
          <div className="mt-4 pt-4 border-t border-border flex items-center justify-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Handcrafted Quality
            </span>
            <span>•</span>
            <span>Easy WhatsApp Confirmation</span>
          </div>

          <p className="text-[11px] text-muted-foreground mt-3 text-center leading-relaxed">
            Clicking will record your order and open WhatsApp with your item list, delivery address, and final total of <strong>₹{totalOrderAmount}</strong>.
          </p>
        </aside>
      </form>
    </div>
  );
}

function Field({ label, testid, as, required, ...rest }) {
  const cls =
    "w-full bg-muted/40 border border-border px-4 py-2.5 rounded-lg outline-none focus:border-terracotta focus:ring-1 focus:ring-terracotta transition text-sm text-foreground";
  return (
    <label className="block">
      <span className="block text-xs sm:text-sm font-medium mb-1.5 text-foreground">
        {label}
      </span>
      {as === "textarea" ? (
        <textarea data-testid={testid} rows={3} required={required} {...rest} className={cls} />
      ) : (
        <input data-testid={testid} required={required} {...rest} className={cls} />
      )}
    </label>
  );
}
